import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { themeQuartz } from 'ag-grid-community';
import '../../config/agGridConfig';
import { adminAPI } from '../../services/api';
import { DeliveryOption } from '../../types';
import DeliveryOptionFormModal from '../../components/admin/DeliveryOptionFormModal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Toast from '../../components/admin/Toast';
import '../../styles/AdminGlobal.css';
import './Admin.css';

const DeliveryOptionsManagement: React.FC = () => {
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<DeliveryOption | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false,
    message: '',
    type: 'success'
  });

  const columnDefs: ColDef[] = [
    { field: 'id', headerName: 'ID', width: 70, sortable: true },
    { 
      field: 'icon', 
      headerName: 'Icon', 
      width: 80,
      cellRenderer: (params: any) => params.value || '-'
    },
    { field: 'name_en', headerName: 'Name (EN)', sortable: true, filter: true, flex: 1 },
    { field: 'name_bn', headerName: 'Name (BN)', sortable: true, filter: true, flex: 1 },
    { 
      field: 'cost', 
      headerName: 'Cost (৳)', 
      sortable: true, 
      width: 120,
      cellRenderer: (params: any) => `৳${Number(params.value).toFixed(2)}`
    },
    { 
      field: 'display_order', 
      headerName: 'Order', 
      sortable: true, 
      width: 100
    },
    { 
      field: 'is_active', 
      headerName: 'Active', 
      width: 100,
      cellRenderer: (params: any) => params.value ? '✓' : '✗'
    },
    { 
      headerName: 'Actions',
      width: 100,
      pinned: 'right',
      lockPosition: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        const buttonStyle = {
          border: 'none',
          borderRadius: '6px',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '16px',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        };
        
        return (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <button 
              onClick={() => handleEdit(params.data)}
              title="Edit Delivery Option"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#007bff',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              ✏️
            </button>
            <button 
              onClick={() => handleDeleteClick(params.data)}
              title="Delete Delivery Option"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#dc3545',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              🗑️
            </button>
          </div>
        );
      }
    }
  ];

  useEffect(() => {
    loadDeliveryOptions();
  }, []);

  const loadDeliveryOptions = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getDeliveryOptions();
      
      let optionsList: DeliveryOption[] = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          optionsList = response.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          optionsList = response.data.results;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          optionsList = response.data.data;
        }
      } else if (Array.isArray(response)) {
        optionsList = response;
      }
      
      setDeliveryOptions(optionsList);
    } catch (error) {
      console.error('Failed to load delivery options:', error);
      setToast({ show: true, message: 'Failed to load delivery options', type: 'error' });
      setDeliveryOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (option: DeliveryOption) => {
    setSelectedOption(option);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (option: DeliveryOption) => {
    setSelectedOption(option);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedOption) return;
    
    try {
      await adminAPI.deleteDeliveryOption(selectedOption.id);
      setToast({ show: true, message: 'Delivery option deleted successfully', type: 'success' });
      loadDeliveryOptions();
    } catch (error) {
      console.error('Delete error:', error);
      setToast({ show: true, message: 'Failed to delete delivery option', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedOption(null);
    }
  };

  const handleSave = async (formData: any) => {
    try {
      if (selectedOption) {
        await adminAPI.updateDeliveryOption(selectedOption.id, formData);
        setToast({ show: true, message: 'Delivery option updated successfully', type: 'success' });
      } else {
        await adminAPI.createDeliveryOption(formData);
        setToast({ show: true, message: 'Delivery option created successfully', type: 'success' });
      }
      setIsModalOpen(false);
      setSelectedOption(null);
      loadDeliveryOptions();
    } catch (error) {
      console.error('Save error:', error);
      throw error;
    }
  };

  const handleAddNew = () => {
    setSelectedOption(null);
    setIsModalOpen(true);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredOptions = Array.isArray(deliveryOptions) ? deliveryOptions.filter((option: DeliveryOption) =>
    option.name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.name_bn?.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  return (
    <div className="admin-page">
      <div className="management-header">
        <div>
          <h3>🚚 Delivery Options Management</h3>
          <p>Manage delivery charges and shipping options</p>
        </div>
        <div className="management-actions">
          <button className="btn btn-success" onClick={handleAddNew}>
            + Add Delivery Option
          </button>
          <button className="btn btn-secondary" onClick={loadDeliveryOptions}>
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="ag-grid-wrapper">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search delivery options..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        <div className="results-info">
          <span>{filteredOptions.length}</span> delivery options found
        </div>

        <div className="ag-grid-container">
          <AgGridReact
            theme={themeQuartz}
            rowData={filteredOptions}
            columnDefs={columnDefs}
            pagination={true}
            paginationPageSize={20}
            loading={loading}
            defaultColDef={{
              resizable: true,
            }}
          />
        </div>
      </div>

      {isModalOpen && (
        <DeliveryOptionFormModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedOption(null);
          }}
          onSave={handleSave}
          deliveryOption={selectedOption}
        />
      )}

      {isDeleteDialogOpen && (
        <ConfirmDialog
          isOpen={isDeleteDialogOpen}
          title="Delete Delivery Option"
          message={`Are you sure you want to delete "${selectedOption?.name_en}"? This action cannot be undone.`}
          onConfirm={handleDelete}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            setSelectedOption(null);
          }}
        />
      )}

      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default DeliveryOptionsManagement;
